// 运行时配置
import './global.less';
import * as React from 'react';
import type {Settings as LayoutSettings} from '@ant-design/pro-layout';
import {getDvaApp, history} from 'umi';
import {BreadcrumbItemProps, message} from 'antd';
import get from 'lodash/get';

import RightContent from '@/components/RightContent';
import Footer from '@/components/Footer';
import {fetchMeRequest} from '@/services/api/user';
import {initializeSentry, setSentryUser} from '@/services/sentry';
import {initializeTracker, trackPageView} from '@/services/tracker';
import {registerServiceWorker} from '@/services/pwa';
import MenuHeader from '@/components/menu/MenuHeader';
import MenuFooter from '@/components/menu/MenuFooter';
import {initializeLocale} from '@/utils/locale';
import {API} from '@/services/api/typings';
import {BackofficeUserModel} from '@/typings/api/user';
import MenuItemRenderer from '@/components/menu/MenuItemRenderer';
import {selectCurrentJourneyConfig} from '@/services/selectors';
import {
  checkUserAccessToPath,
  getDefaultSolution,
  getPermissionsSet,
  getSolutionFromPath,
  getUserSolutions,
} from './utils/access';
import {isLandingPath} from '@/utils/pathUtils';

import {getGlobalInitialState} from './utils/persistStore';
import {PubSubProvider} from '@/context/PubSubContext';
import {KeyboardShortcutsProvider} from '@/context/KeyboardShortcuts';
import ErrorBoundary from '@/components/ErrorBoundary';
import {helpCenterLayout} from './app_helpcenter';
import {BUILD_COMMIT_HASH} from '@/constants/version';

// Hidden build version: expose the short git SHA (injected via Umi `define`)
// on window so e2e/canary flows can read which commit this backoffice is running.
window.__COMMIT_HASH__ = BUILD_COMMIT_HASH;

initializeSentry();
initializeTracker();
initializeLocale();
registerServiceWorker();

message.config({
  maxCount: 1,
});

const loginPath = '/user/login';
// '/sso' must stay here: the SSO callback lands unauthenticated and would be
// bounced to /user/login (losing its ?code) before it can exchange it.
const ALLOWED_PATHS = ['/user', '/mimic', '/set-password', '/sso'];
const redirectToLogin = () => {
  const {pathname} = window.location;
  const path = isLandingPath(pathname) ? `${loginPath}?redirect=${pathname}` : loginPath;

  history.push(path);
};

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<IInitialState & {
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  selectedSolution?: string;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  if (APP_ENTRY === 'help_center')
    return {}

  const fetchUserInfo = async (): Promise<BackofficeUserModel> => {
    try {
      const APICall = await fetchMeRequest();
      const status = get(APICall, 'response.status');
      const data = get(APICall, 'data');
      if (status === 200) {
        if (window.location.pathname === '/') {
          history.push('/check');
        }
        setSentryUser(data);
        return data;
      } else {
        redirectToLogin();
      }
    } catch (error) {
      redirectToLogin();
    }
    return undefined;
  };
  // If it is a login page, do not execute
  const { location: { pathname } } = history;
  if (!ALLOWED_PATHS.some(path => pathname.includes(path))) {
    const currentUser = await fetchUserInfo();
    if (currentUser)
      currentUser.permissionsSet = getPermissionsSet(currentUser);
    const solutions = getUserSolutions(currentUser);
    const selectedSolution = getSolutionFromPath(pathname) || getDefaultSolution(currentUser);
    return {
      fetchUserInfo,
      currentUser,
      settingsLoaded: true,
      solutions,
      selectedSolution,
      settings: {},
    };
  }
  // TODO: specify default solution or if the user should only see one of them

  return {
    fetchUserInfo,
    selectedSolution: 'leadGeneration',
    settings: {},
  };
}

type IInitialState = {
  collapsed?: boolean,
  hideFooter?: boolean,
  disableContentMargin?: boolean,
  config?: any,
  settings?: Record<string, unknown>,
  currentUser?: BackofficeUserModel,
  selectedSolution?: string,
  breadcrumb?: BreadcrumbItemProps[],
  solutions: []
}

type ILayoutFunctionProps = {
  initialState: IInitialState,
  currentUser: BackofficeUserModel,
  setInitialState: (state: IInitialState) => void
}

const filterMenu = (initialState: IInitialState, menuData) => {
  const user = initialState?.currentUser;
  const selectedSolution = initialState.selectedSolution;
  const journeyConfig = selectCurrentJourneyConfig(window.getState());
  return menuData.filter(menu => {
    const { path, solution, journeyConfigAccessKey } = menu;
    const hasUserAccess = checkUserAccessToPath(user, path);
    if (!hasUserAccess) return false;

    if (solution && selectedSolution !== solution) return false;

    if (journeyConfigAccessKey) {
      return get(journeyConfig, journeyConfigAccessKey);
    }

    if (Array.isArray(menu.children) && menu.children.length > 0) {
      menu.children = filterMenu(initialState, menu.children);
      // Every child was filtered out. pro-layout renders a childless group as a
      // plain item linking to the group's own path (BaseMenu only builds a
      // SubMenu when children.length > 0), and no group route has a page behind
      // it — so the group has to go with its children.
      if (menu.children.length === 0) return false;
    }
    return true;
  });
};

export const layout = APP_ENTRY === 'help_center'? helpCenterLayout : ({ initialState, setInitialState }: ILayoutFunctionProps) => {
  return {
    rightContentRender: () => <RightContent />,
    siderWidth: 300,
    siderActions: [],
    disableContentMargin: !!initialState?.disableContentMargin,
    menuHeaderRender: () => <MenuHeader />,
    menuFooterRender: () => <MenuFooter />,
    onMenuHeaderClick: () => {
    },
    // loading: () => <div>LOading</div>,
    ErrorBoundary,
    footerRender: () => !initialState.hideFooter && <Footer />,
    onPageChange: () => {
      if (window.innerWidth < 992) {
        if (!initialState.collapsed) {
          setInitialState({
            ...initialState,
            collapsed: true,
          });
        }
      }
      trackPageView();
      const { location } = history;
      // If not logged in, redirect to login
      if (initialState?.settingsLoaded && !initialState?.currentUser && !ALLOWED_PATHS.some(path => location?.pathname?.includes(path)))
        return redirectToLogin();
    },
    onCollapse: (collapsed) =>
      setInitialState({
        ...initialState,
        collapsed,
      }),
    postMenuData: (menuData) => filterMenu(initialState, menuData),
    menuItemRender: (item, dom) => <MenuItemRenderer item={item} dom={dom} collapsed={!!initialState?.collapsed} />,
    headerContentRender: () => <></>,
    collapsedButtonRender: false,
    collapsed: initialState?.collapsed,
    ...initialState?.settings,
    journeyConfig: initialState?.config,
  };
};


// Phones get a smaller type scale. Every antd size (headings, controls, table text)
// derives from the fontSize seed token, so one override scales them coherently —
// LESS variables can't do this, they are baked in at compile time.
// 767px mirrors useIsMobile / @screen-mobile-max; applied at startup, not on resize.
export const antd = (memo: any) => {
  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
    memo.theme = {
      ...memo.theme,
      token: {
        ...memo.theme?.token,
        fontSize: 13,
      },
    };
  }
  return memo;
};

export const dva = {
  config: {
    initialState: getGlobalInitialState(),
    onError: () => {
    },
  },
};

window.g_app = () => getDvaApp();
window.getState = () => getDvaApp()._store.getState();

const mockConsoleMethod = (realConsoleMethod) => {
  const ignoredMessages = [
    '[React Intl]',
    '"key" prop',
  ];

  return (message = '', ...args) => {
    const containsIgnoredMessage = ignoredMessages.some(
      (ignoredMessage) => typeof message === 'string' && message.includes(ignoredMessage),
    );

    if (!containsIgnoredMessage) {
      realConsoleMethod(message, ...args);
    }
  };
};

console.warn = mockConsoleMethod(console.warn);
console.error = mockConsoleMethod(console.error);

export function rootContainer(container) {
  return (
    <PubSubProvider>
      <KeyboardShortcutsProvider>
        {container}
      </KeyboardShortcutsProvider>
    </PubSubProvider>
  );
}
